import { Component, inject, Input } from '@angular/core'
import { MatIcon } from '@angular/material/icon'
import { Call, CallSessionStatus, CallsService, CallStatus, CallWithLatestSession } from '../calls.service'
import { MatDialog } from '@angular/material/dialog'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { MatTooltipModule } from '@angular/material/tooltip'
import { RouterLink } from '@angular/router'
import { MatIconButton } from '@angular/material/button'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatIconButtonSizesModule } from 'mat-icon-button-sizes'
import { subMinutes } from 'date-fns'
import { formatTimeUntil } from '../utils'
import { ToastService } from '../toast.service'
import { firstValueFrom } from 'rxjs'

@Component({
  selector: 'app-ongoing-meeting-card',
  standalone: true,
  imports: [MatIcon, TranslateModule, MatTooltipModule, MatIconButton, RouterLink, MatMenu, MatMenuItem, MatMenuTrigger, MatIconButtonSizesModule],
  templateUrl: './ongoing-meeting-card.component.html',
  styleUrl: './ongoing-meeting-card.component.scss',
})
export class OngoingMeetingCardComponent {
  @Input({ required: true })
  call!: CallWithLatestSession

  @Input({ required: true })
  isExpanded!: boolean

  @Input({ required: true })
  useHighlight!: boolean

  translateService = inject(TranslateService)
  callsService = inject(CallsService)
  dialog = inject(MatDialog)
  toastService = inject(ToastService)
  switchToNextSubCallMutation = this.callsService.switchToNextSubCallMutation()
  joinNowMutation = this.callsService.joinNowMutation()

  pausing = false
  resuming = false
  joining = false

  hangUp(call: Call): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('CALLS.HANG_UP.DIALOG_TITLE'),
        description: this.translateService.instant('CALLS.HANG_UP.DIALOG_BODY', { callName: call.name }),
        confirmLabel: this.translateService.instant('COMMON.YES'),
      },
    })
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.callsService.hangUpCall(call.id)
      }
    })
  }

  join(call: Call) {
    window.open(call.link!)
  }

  pauseRecording(call: Call) {
    this.callsService.pauseRecording(call.id)
    this.pausing = true
  }

  resumeRecording(call: Call) {
    this.callsService.resumeRecording(call.id)
    this.resuming = true
  }
  async switchToNextSubMeeting(call: Call) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('CALLS.ONGOING_CALLS.PROCEED_TO_NEXT_SUB_CALL.TITLE'),
        description: this.translateService.instant('CALLS.ONGOING_CALLS.PROCEED_TO_NEXT_SUB_CALL.DESCRIPTION'),
      },
    })
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.switchToNextSubCallMutation.mutateAsync(call.id)
      }
    })
  }

  get displayStatusButtons(): boolean {
    return !this.pausing && !this.resuming
  }

  get statusMessage(): string | null {
    if (this.call.status === CallStatus.WAITING_TO_BE_QUEUED) {
      return this.translateService.instant('CALLS.ONGOING_CALLS.STATUS.QUEUED')
    }
    if (this.call.latestCallSession === null) {
      return null
    }

    if (this.call.latestCallSession.status === CallSessionStatus.WAITING_TO_START) {
      if (this.joining) {
        return this.translateService.instant('CALLS.ONGOING_CALLS.STATUS.JOINING')
      }
      const botJoinTime = subMinutes(new Date(this.call.date), 5)
      const timeUntilJoin = formatTimeUntil(botJoinTime)
      if (timeUntilJoin !== null) {
        if (timeUntilJoin === '0m') {
          return this.translateService.instant('CALLS.ONGOING_CALLS.STATUS.BOT_JOINS_IN_LESS_THAN_A_MINUTE')
        }
        return this.translateService.instant('CALLS.ONGOING_CALLS.STATUS.BOT_JOINS_IN', { time: timeUntilJoin })
      }
      // Join time passed but status hasn't updated yet - bot is clicking join
      return this.translateService.instant('CALLS.ONGOING_CALLS.STATUS.CONNECTING')
    }

    if (this.joining) {
      this.joining = false
    }

    const statusMap = new Map<CallSessionStatus, string>([
      [CallSessionStatus.QUEUED, 'CALLS.ONGOING_CALLS.STATUS.QUEUED'],
      [CallSessionStatus.CONNECTING, 'CALLS.ONGOING_CALLS.STATUS.CONNECTING'],
      [CallSessionStatus.WAITING_TO_BE_ADMITTED, 'CALLS.ONGOING_CALLS.STATUS.WAITING_TO_BE_ADMITTED'],
      [CallSessionStatus.HANGING_UP, 'CALLS.ONGOING_CALLS.STATUS.HANGING_UP'],
    ])
    if (statusMap.has(this.call.latestCallSession.status)) {
      return this.translateService.instant(statusMap.get(this.call.latestCallSession.status)!)
    }

    if (this.call.latestCallSession.status === CallSessionStatus.CONNECTED) {
      if (this.resuming) {
        this.resuming = false
      } else if (this.pausing) {
        return this.translateService.instant('CALLS.ONGOING_CALLS.STATUS.PAUSING')
      }
      return this.translateService.instant('CALLS.ONGOING_CALLS.STATUS.RECORDING')
    }

    if (this.call.latestCallSession.status === CallSessionStatus.PAUSED) {
      if (this.pausing) {
        this.pausing = false
      } else if (this.resuming) {
        return this.translateService.instant('CALLS.ONGOING_CALLS.STATUS.RESUMING')
      }
      return this.translateService.instant('CALLS.ONGOING_CALLS.STATUS.PAUSED')
    }

    return null
  }

  get displaySkipToNextSubMeetingButton(): boolean {
    return (
      this.call.agendaType === 'MULTIPLE' &&
      this.call.latestCallSession !== null &&
      (this.call.latestCallSession.status === CallSessionStatus.CONNECTED || this.call.latestCallSession.status === CallSessionStatus.PAUSED)
    )
  }

  get currentParticipantText(): string {
    const count = Math.max(this.call.currentParticipantCount! - 1, 0)
    const key = count === 1 ? 'COMMON.ONGOING_PARTICIPANTS_ONE' : 'COMMON.ONGOING_PARTICIPANTS_PLURAL'
    return this.translateService.instant(key, { count })
  }

  get canJoinNow(): boolean {
    if (this.call.latestCallSession?.status !== CallSessionStatus.WAITING_TO_START) {
      return false
    }
    const botJoinTime = subMinutes(new Date(this.call.date), 5)
    return formatTimeUntil(botJoinTime) !== null
  }

  async joinNow(call: Call): Promise<void> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('CALLS.JOIN_IMMEDIATELY.DIALOG_TITLE'),
        description: this.translateService.instant('CALLS.JOIN_IMMEDIATELY.DIALOG_DESCRIPTION'),
        confirmLabel: this.translateService.instant('COMMON.YES'),
      },
    })
    const result = await firstValueFrom(dialogRef.afterClosed())
    if (result) {
      this.joining = true
      await this.joinNowMutation.mutateAsync(call.id)
      this.toastService.show('CALLS.JOIN_IMMEDIATELY.SUCCESS_CONFIRMATION')
    }
  }
}
